import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DownloadSimple, Rocket, TiktokLogo, YoutubeLogo, InstagramLogo } from "@phosphor-icons/react";

const PLATFORM_ICONS = {
    tiktok: TiktokLogo,
    youtube: YoutubeLogo,
    instagram: InstagramLogo,
};

const PLATFORM_GRADIENTS = {
    tiktok: "from-[#FF0050]/30 via-[#0A0A0A] to-[#00F2EA]/15",
    youtube: "from-[#FF0000]/25 via-[#0A0A0A] to-[#660000]/10",
    instagram: "from-[#833AB4]/25 via-[#FD1D1D]/15 to-[#FCB045]/10",
};

const PLATFORM_BORDERS = {
    tiktok: "hover:border-[#FF0050]/60",
    youtube: "hover:border-[#FF0000]/60",
    instagram: "hover:border-[#E1306C]/60",
};

export default function DeploymentHub() {
    const [guides, setGuides] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        api.get("/deployment-hub").then((r) => setGuides(r.data)).catch(() => {});
    }, []);

    const downloadPdf = async (g) => {
        if (!g.pdf_file_id) return;
        setDownloadingId(g.id);
        try {
            const r = await api.get(`/files/${g.pdf_file_id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            const safe = g.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
            a.download = `${safe}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("GUIDE DOWNLOADED");
        } catch (err) {
            toast.error("Download failed");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <section data-testid="deployment-hub-section" className="bg-[#0A0A0A] border border-white/10 relative overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Rocket size={22} className="text-electric-blue" weight="duotone" />
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    </div>
                    <div>
                        <p className="label-mono text-electric-blue">THE DEPLOYMENT HUB</p>
                        <h2 className="font-heading font-bold text-lg">Platform 101 Rapid Guides</h2>
                    </div>
                </div>
                <span className="hidden md:inline label-mono text-[10px] text-muted-foreground">
                    SHIP THE FINISHED PRODUCT
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
                {guides.map((g, i) => {
                    const Icon = PLATFORM_ICONS[g.platform_key] || Rocket;
                    const gradient = PLATFORM_GRADIENTS[g.platform_key] || "from-electric-blue/20 to-black";
                    const borderHover = PLATFORM_BORDERS[g.platform_key] || "hover:border-electric-blue/60";
                    return (
                        <div
                            key={g.id}
                            data-testid={`deployment-card-${i}`}
                            className={`relative bg-[#060606] border border-white/10 ${borderHover} transition-all hover:-translate-y-1 group flex flex-col overflow-hidden`}
                        >
                            <div className={`relative aspect-[16/9] bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                                <Icon
                                    size={84}
                                    weight="duotone"
                                    style={{ color: g.platform_color || "#007BFF" }}
                                    className="opacity-90 group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-3 left-3 px-2 py-1 bg-black/70 border border-white/10">
                                    <span className="label-mono text-[9px] uppercase" style={{ color: g.platform_color || "#007BFF" }}>
                                        {g.category}
                                    </span>
                                </div>
                                <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 border border-emerald-500/40">
                                    <span className="label-mono text-[9px] text-emerald-400">3-MIN READ</span>
                                </div>
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                                <h3 className="font-heading font-bold text-sm mb-2 line-clamp-2 group-hover:text-electric-blue transition-colors">
                                    {g.title.replace("Deployment Hub: ", "")}
                                </h3>
                                <p className="text-xs text-muted-foreground mb-4 leading-relaxed line-clamp-3 flex-1">
                                    {g.description}
                                </p>
                                <button
                                    onClick={() => downloadPdf(g)}
                                    disabled={downloadingId === g.id}
                                    data-testid={`deployment-download-${i}`}
                                    className="inline-flex items-center justify-center gap-2 bg-transparent border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-3 py-2 text-xs label-mono transition-all disabled:opacity-50"
                                >
                                    <DownloadSimple size={14} weight="bold" />
                                    {downloadingId === g.id ? "DOWNLOADING..." : "DOWNLOAD GUIDE"}
                                </button>
                            </div>
                        </div>
                    );
                })}
                {guides.length === 0 && (
                    <div className="col-span-full p-8 text-center">
                        <Rocket size={32} className="text-muted-foreground mx-auto mb-3" />
                        <p className="label-mono text-muted-foreground">DEPLOYMENT HUB INITIALIZING...</p>
                    </div>
                )}
            </div>
        </section>
    );
}
